import { formatPrice } from "../../i18n/currency";

export type ReferenceLayout = "single-room" | "two-room";
export type ReferenceHeater = "electric" | "wood";

export interface ReferenceMaterialRow {
	label: string;
	value: string;
}

export interface ReferenceDecision {
	label: string;
	text: string;
}

export interface ReferenceBudgetRow {
	/** The pricing-summary allowance this row quotes. */
	allowanceId:
		| "professional-services"
		| "delivery-rounding"
		| "hidden-supplies";
	label: string;
	amountSek: number;
}

export interface ReferenceDesignDefinition {
	path: string;
	title: string;
	seoTitle: string;
	seoDescription: string;
	headline: string;
	kicker: string;
	deck: string;
	badge: string;
	blurb: string;
	readMin: number;
	widthM: number;
	lengthM: number;
	areaLabel: string;
	roofLabel: string;
	layout: ReferenceLayout;
	heater: ReferenceHeater;
	capacity: number;
	recommendedKw: number;
	/** Heater sizing volume incl. the glass penalty, as the BBR tab shows it. */
	effectiveVolumeM3: number;
	/** The Pris tab's material figure: the exact sum of the priced BOM rows. */
	materialCostSek: number;
	diyBudgetSek: number;
	budgetRows: readonly ReferenceBudgetRow[];
	materialRows: readonly ReferenceMaterialRow[];
	designReasons: readonly string[];
	decisions: readonly ReferenceDecision[];
	planDescription: string;
	roomLabels: readonly [string, string?];
	builderPath: string;
}

const commonWoodDecisions: readonly ReferenceDecision[] = [
	{
		label: "Kaminen.",
		text: "Välj modell innan skyddsavstånd, golvskydd och takgenomföring låses.",
	},
	{
		label: "Taklasten.",
		text: "Verifiera snözonen innan takbjälkar och eventuella förstärkningar beställs.",
	},
	{
		label: "Ventilationen.",
		text: "Bestäm öppningarnas placering och area mot kaminleverantörens anvisning.",
	},
	{
		label: "Myndighetskraven.",
		text: "Kontrollera bygglovsläge och anmälan för eldstad och rökkanal med kommunen.",
	},
];

export const REFERENCE_DESIGNS: readonly ReferenceDesignDefinition[] = [
	{
		path: "/ritningar/liten-vedeldad-utomhusbastu-6m2",
		title: "Liten vedeldad utomhusbastu 6 m² – referensdesign",
		seoTitle: "Liten vedeldad bastu 6 m² – ritning och pris | Bygg en bastu",
		seoDescription:
			"Digital ritning för en liten vedeldad utomhusbastu på 6 m². Se planlösning, materialmängder, kostnad och öppna modellen i 3D.",
		headline: "Liten vedeldad utomhusbastu på 6 m²",
		kicker: "Kompakt referensförslag · två rum",
		deck: "Två gånger tre meter räcker till ett litet omklädningsrum och ett avskilt basturum. Planen prioriterar funktionen framför fria golvytor och visar var den kompakta lösningen kräver extra noggrannhet.",
		badge: "6 m² · ved",
		blurb:
			"Kompakt 2,0 × 3,0 meter med två rum, vedkamin, 6 kW modellbehov och beräknad DIY-budget.",
		readMin: 7,
		widthM: 2,
		lengthM: 3,
		areaLabel: "6,0 m²",
		roofLabel: "Nocktak 22°",
		layout: "two-room",
		heater: "wood",
		capacity: 2,
		recommendedKw: 6,
		effectiveVolumeM3: 7.8,
		materialCostSek: 92_824,
		diyBudgetSek: 109_000,
		budgetRows: [
			{
				allowanceId: "professional-services",
				label: "Skorsten och besiktning, kompetenspost",
				amountSek: 9000,
			},
			{
				allowanceId: "delivery-rounding",
				label: "Leverans och avrundning",
				amountSek: 4200,
			},
			{
				allowanceId: "hidden-supplies",
				label: "Småmaterial som annars missas",
				amountSek: 2800,
			},
		],
		materialRows: [
			{
				label: "Grund",
				value: "9 plintar, 9 stolpskor, 8 m² markduk och dräneringsgrus",
			},
			{
				label: "Stomme",
				value: "7 lpm bärlina, 15 lpm golvbjälkar, 71 lpm väggreglar",
			},
			{ label: "Isolering", value: "22 m² vägg, 7 m² tak och 6 m² golv" },
			{
				label: "Invändigt",
				value: "18 m² bastufolie samt panel och lavvirke för två personer",
			},
			{ label: "Fasad", value: "221 lpm ytterpanel inklusive 10 % spill" },
			{ label: "Tak", value: "9,7 m² råspont, underlagspapp och ytpapp" },
			{ label: "Öppningar", value: "Ytterdörr, mellandörr och ett fönster" },
			{
				label: "Eldstad",
				value: "Vedkamin, modellberäknad bastuskorsten och takdetaljer",
			},
		],
		designReasons: [
			"Vi valde 2,0 meters bredd för att hålla huset litet utan att göra lavarna för korta. Den smala kroppen lämnar få alternativa möbleringar, vilket är en fördel när målet är låg byggnadsarea och en nackdel om flera ska byta om samtidigt.",
			"Mellanväggen delar längden i två delar om cirka 1,5 meter. Omklädningsdelen blir en sluss, inte ett sällskapsrum. Bastudelen får den större delen av den användbara vägglängden genom L-formade lavar.",
			"Beräkningsmotorn anger 6 kW för den här grundmodellen. Det är ett dimensioneringsvärde, inte ett produktval; glas, oisolerade ytor och vald kamin kan ändra behovet.",
		],
		decisions: [
			{
				label: "Passagen.",
				text: "Provmät dörrslag, kamin och lavhörn eftersom varje decimeter påverkar rörelsen.",
			},
			...commonWoodDecisions,
		],
		planDescription:
			"En två gånger tre meter stor byggnad med en liten omklädningsdel, ett basturum, vedkamin och L-formade lavar.",
		roomLabels: ["Omklädning", "Basturum"],
		builderPath: "/builder/tvarum?w=2&l=3&heat=wood&people=2",
	},
	{
		path: "/ritningar/elektrisk-utomhusbastu-6m2",
		title: "Elektrisk utomhusbastu 6,25 m² – referensdesign",
		seoTitle: "Elektrisk utomhusbastu 6 m² – ritning för fyra | Bygg en bastu",
		seoDescription:
			"Digital ritning för en elektrisk utomhusbastu på 2,5 × 2,5 meter. Se lavar, aggregatbehov, elkrav, material och kostnad.",
		headline: "Elektrisk utomhusbastu för fyra på 6,25 m²",
		kicker: "Referensförslag · ett basturum",
		deck: "En kvadratisk bastu utan omklädningsrum. Alla 6,25 kvadratmeter används till värmerum, vilket ger bättre lavar men ställer högre krav på effekt och på en torr entré utanför byggnaden.",
		badge: "6,25 m² · el",
		blurb:
			"Kvadratisk 2,5 × 2,5 meter för fyra, elektriskt aggregat, 400 V-kontrollpunkt och egen materialkalkyl.",
		readMin: 7,
		widthM: 2.5,
		lengthM: 2.5,
		areaLabel: "6,25 m²",
		roofLabel: "Pulpettak 8°",
		layout: "single-room",
		heater: "electric",
		capacity: 4,
		recommendedKw: 12,
		effectiveVolumeM3: 15.2,
		materialCostSek: 76_192,
		diyBudgetSek: 89_000,
		budgetRows: [
			{
				allowanceId: "professional-services",
				label: "Behörig elinstallation, kompetenspost",
				amountSek: 6500,
			},
			{
				allowanceId: "delivery-rounding",
				label: "Leverans och avrundning",
				amountSek: 3600,
			},
			{
				allowanceId: "hidden-supplies",
				label: "Småmaterial som annars missas",
				amountSek: 2800,
			},
		],
		materialRows: [
			{
				label: "Grund",
				value: "9 plintar, 9 stolpskor, 9 m² markduk och dräneringsgrus",
			},
			{
				label: "Stomme",
				value: "8 lpm bärlina, 15 lpm golvbjälkar, 71 lpm väggreglar",
			},
			{ label: "Isolering", value: "23 m² vägg, 7 m² tak och 7 m² golv" },
			{
				label: "Invändigt",
				value: "30 m² bastufolie samt panel och två lavnivåer",
			},
			{ label: "Fasad", value: "228 lpm ytterpanel inklusive 10 % spill" },
			{ label: "Tak", value: "9,3 m² råspont, underlagspapp och ytpapp" },
			{
				label: "Entré",
				value: "En utåtgående bastudörr; ingen separat omklädningsdel",
			},
			{
				label: "Värme",
				value: "Elektriskt aggregat och separat projekterad matning",
			},
		],
		designReasons: [
			"Kvadratmåttet 2,5 × 2,5 meter ger mer sammanhängande lavlängd än ett smalt rum med samma area. Vi använder bakväggen och ena sidoväggen och lämnar en tydlig gånglinje från dörren till aggregatet.",
			"Pulpettaket på 8 grader håller byggnaden låg och gör vattenriktningen entydig. Snölasten försvinner inte för att taket är enkelt; bjälkdimensionen måste fortfarande räknas för platsen.",
			"Glaset i bastudörren räknas som extra volym, så grundmodellen motsvarar 15,2 m³. Det ger ett beräknat behov på 12 kW, vilket kräver 400 V trefas; modellens kontrollpunkt är 20 A, och planeraren varnar för att det ofta kräver en högre huvudsäkring. Kontrollera huvudsäkring och ledningsväg med behörigt elinstallationsföretag innan aggregat köps.",
		],
		decisions: [
			{
				label: "Entrén.",
				text: "Planera en torr avställningsyta utanför eftersom modellen saknar omklädningsrum.",
			},
			{
				label: "Elkapaciteten.",
				text: "Verifiera 400 V, huvudsäkring och kabelväg innan aggregatet väljs.",
			},
			{
				label: "Aggregatet.",
				text: "Lås produktens skyddsavstånd innan dörr, räcke och nedre lav slutplaceras.",
			},
			{
				label: "Taklasten.",
				text: "Verifiera snözon och takbjälkar trots den korta spännvidden.",
			},
			{
				label: "Ventilationen.",
				text: "Samordna till- och frånluft med aggregatets installationsanvisning.",
			},
		],
		planDescription:
			"Ett kvadratiskt basturum på två och en halv gånger två och en halv meter med elektriskt aggregat och L-formade lavar för fyra.",
		roomLabels: ["Basturum för fyra"],
		builderPath: "/builder/fyrkantig?w=2.5&l=2.5&heat=electric&people=4",
	},
	{
		path: "/ritningar/vedeldad-utomhusbastu-10m2",
		title: "Vedeldad utomhusbastu 10 m² – referensdesign",
		seoTitle: "Vedeldad utomhusbastu 10 m² – ritning | Bygg en bastu",
		seoDescription:
			"Digital planritning för en vedeldad utomhusbastu på 10 m². Se mått, planlösning, materialmängder, kostnadskalkyl och öppna samma design i 3D.",
		headline: "Vedeldad utomhusbastu på 10 m²",
		kicker: "Digitalt referensförslag · tvårumsbastu",
		deck: "Ett konkret sätt att använda tio kvadratmeter: 2,5 × 4,0 meter, hälften omklädning och hälften basturum. Här redovisar vi modellen, materialmängderna och de val som fortfarande måste avgöras på plats.",
		badge: "10 m² · ved",
		blurb:
			"Planritning för 2,5 × 4,0 meter med omklädning, vedkamin, materialmängder och öppet redovisad kostnad.",
		readMin: 8,
		widthM: 2.5,
		lengthM: 4,
		areaLabel: "10,0 m²",
		roofLabel: "Nocktak 22°",
		layout: "two-room",
		heater: "wood",
		capacity: 4,
		recommendedKw: 8,
		effectiveVolumeM3: 12,
		materialCostSek: 115_772,
		diyBudgetSek: 134_000,
		budgetRows: [
			{
				allowanceId: "professional-services",
				label: "Skorsten och besiktning, kompetenspost",
				amountSek: 9000,
			},
			{
				allowanceId: "delivery-rounding",
				label: "Leverans och avrundning",
				amountSek: 5500,
			},
			{
				allowanceId: "hidden-supplies",
				label: "Småmaterial som annars missas",
				amountSek: 3800,
			},
		],
		materialRows: [
			{
				label: "Grund",
				value: "9 plintar, 9 stolpskor, 13 m² markduk och dräneringsgrus",
			},
			{
				label: "Stomme",
				value: "8 lpm bärlina, 24 lpm golvbjälkar, 100 lpm väggreglar",
			},
			{ label: "Isolering", value: "29 m² vägg, 11 m² tak och 10 m² golv" },
			{
				label: "Invändigt",
				value: "26 m² bastufolie, 2 paket alutejp, panel och lavvirke",
			},
			{
				label: "Fasad",
				value: "29 m² vindskydd, 290 lpm ytterpanel inklusive 10 % spill",
			},
			{ label: "Tak", value: "15,1 m² råspont, underlagspapp och ytpapp" },
			{ label: "Öppningar", value: "Ytterdörr, mellandörr och ett fönster" },
			{
				label: "Eldstad",
				value: "Vedkamin samt modellberäknad bastuskorsten och takdetaljer",
			},
		],
		designReasons: [
			"Vi valde 2,5 meters bredd för att hålla spännvidderna rimliga och få en byggnad som är lätt att placera. Fyra meters längd ger exakt 10 m². Mellanväggen står mitt i huset, med ungefär fem kvadratmeter på vardera sidan.",
			"Kaminen ligger nära mellanväggen. Då kan veden hanteras nära entrén och skorstenen hållas borta från lavarnas sittplatser. Placeringen är en startpunkt; vald kamin bestämmer skyddsavstånd och takgenomföring.",
			"Nocktaket är satt till 22 grader och takpapp i modellen. Det är en enkel form med få specialdetaljer. Takbjälkarnas slutliga dimension beror på snözonen där bastun ska stå.",
		],
		decisions: [
			{
				label: "Tomten.",
				text: "Kontrollera mark, höjdläge, avstånd till gräns och andra byggnader.",
			},
			...commonWoodDecisions,
		],
		planDescription:
			"En byggnad på två och en halv gånger fyra meter, delad i omklädningsrum och basturum med vedkamin och L-formade lavar.",
		roomLabels: ["Omklädning", "Basturum"],
		builderPath: "/builder/tvarum?w=2.5&l=4&heat=wood&people=4",
	},
	{
		path: "/ritningar/vedeldad-utomhusbastu-15m2",
		title: "Vedeldad utomhusbastu 15 m² – referensdesign",
		seoTitle: "Vedeldad utomhusbastu 15 m² – ritning för sex | Bygg en bastu",
		seoDescription:
			"Digital ritning för en vedeldad utomhusbastu på 15 m² med relaxrum. Se planlösning, kaminbehov, material och kostnad.",
		headline: "Vedeldad utomhusbastu på 15 m² med relaxrum",
		kicker: "Rymligt referensförslag · sex personer",
		deck: "Tre gånger fem meter ger plats för ett verkligt relaxrum, längre lavar och bättre passage runt kaminen. Den extra ytan kostar både material och uppvärmning, så sidan visar vad som faktiskt växer jämfört med tiokvadratsmodellen.",
		badge: "15 m² · ved",
		blurb:
			"Rymlig 3,0 × 5,0 meter för sex med relaxrum, 17,2 m³ effektiv volym och större material- och kostnadsposter.",
		readMin: 8,
		widthM: 3,
		lengthM: 5,
		areaLabel: "15,0 m²",
		roofLabel: "Nocktak 22°",
		layout: "two-room",
		heater: "wood",
		capacity: 6,
		recommendedKw: 15,
		effectiveVolumeM3: 17.2,
		materialCostSek: 139_282,
		diyBudgetSek: 160_500,
		budgetRows: [
			{
				allowanceId: "professional-services",
				label: "Skorsten och besiktning, kompetenspost",
				amountSek: 9000,
			},
			{
				allowanceId: "delivery-rounding",
				label: "Leverans och avrundning",
				amountSek: 7100,
			},
			{
				allowanceId: "hidden-supplies",
				label: "Småmaterial som annars missas",
				amountSek: 5100,
			},
		],
		materialRows: [
			{
				label: "Grund",
				value: "12 plintar, 12 stolpskor, 19 m² markduk och dräneringsgrus",
			},
			{
				label: "Stomme",
				value: "13 lpm bärlina, 30 lpm golvbjälkar, 112 lpm väggreglar",
			},
			{ label: "Isolering", value: "36 m² vägg, 17 m² tak och 15 m² golv" },
			{
				label: "Invändigt",
				value: "34 m² bastufolie samt längre panel- och lavlängder",
			},
			{ label: "Fasad", value: "360 lpm ytterpanel inklusive 10 % spill" },
			{ label: "Tak", value: "21,8 m² råspont, underlagspapp och ytpapp" },
			{ label: "Öppningar", value: "Ytterdörr, mellandörr och ett fönster" },
			{
				label: "Eldstad",
				value: "Vedkamin, modellberäknad bastuskorsten och takdetaljer",
			},
		],
		designReasons: [
			"Tre meters bredd ger bättre passage och djupare möbleringsmån än de mindre modellerna. Fem meters längd delas i två lika delar, så relaxrummet kan användas för sittplatser och förvaring i stället för att bara fungera som luftsluss.",
			"Den längre bakväggen ger sammanhängande lavar för upp till sex personer. Kapaciteten är ett planeringsmål från wizarden; verklig komfort beror på lavbredd, aggregatets skyddszon och hur tätt man vill sitta.",
			"Grundmodellen motsvarar 17,2 m³ effektiv volym. Det är mer än det största effektsteget på 15 kW täcker, och planeraren varnar för det: välj en kamin vars rumsvolym enligt tillverkaren täcker 17,2 m³. Extra glas eller tungt murverk ökar behovet ytterligare.",
		],
		decisions: [
			{
				label: "Relaxrummet.",
				text: "Bestäm om ytan ska vara torr sittplats, ombyte eller innehålla vatteninstallationer; kalkylen innehåller ingen dusch.",
			},
			...commonWoodDecisions,
		],
		planDescription:
			"En tre gånger fem meter stor byggnad med relaxrum, basturum, vedkamin och långa L-formade lavar för sex personer.",
		roomLabels: ["Relaxrum", "Basturum för sex"],
		builderPath: "/builder/tvarum?w=3&l=5&heat=wood&people=6",
	},
] as const;

export const REFERENCE_DESIGN_PATHS = REFERENCE_DESIGNS.map(
	(design) => design.path,
);

export function getReferenceDesign(
	path: string,
): ReferenceDesignDefinition | undefined {
	return REFERENCE_DESIGNS.find((design) => design.path === path);
}

/** Formats an amount exactly as the builder's Pris tab does on the Swedish market. */
export function formatSek(amount: number): string {
	return formatPrice(amount, "se");
}
